import type { UseQueryResult } from '@tanstack/react-query';
import { useRelatedOrgs } from '@/lib/relationships/use-related-orgs';
import type { OrgPersona } from '@/lib/relationships/relationships.types';

/** An org (agency or client) that a caller may onboard a worker under. */
export interface AssignableOrg {
  tenantId: string;
  displayName: string;
  persona: 'CLIENT' | 'AGENCY';
}

/**
 * Orgs the caller may onboard a worker under (ENT-269).
 *
 * Sourced from `relatedOrgs(kinds:[MANAGES], direction:SOURCE)` — the caller's
 * managed-org set — filtered to persona ∈ {CLIENT, AGENCY}:
 *   SA     → all overseen agencies + all clients (platform-oversight edges)
 *   Agency → its managed clients (AGENCY rows are absent — agencies don't
 *            oversee other agencies; the caller prepends self separately)
 *   Client → empty (clients don't manage orgs)
 *
 * `enabled` is ANDed with the active-context check inside `useRelatedOrgs`.
 */
export function useAssignableOrgs(
  q: string | undefined,
  enabled: boolean,
): UseQueryResult<AssignableOrg[]> {
  return useRelatedOrgs(
    { kinds: ['MANAGES'], direction: 'SOURCE', q: q || undefined, enabled },
    {
      select: (rows): AssignableOrg[] =>
        rows
          .filter((o): o is typeof o & { persona: OrgPersona } =>
            o.persona === 'CLIENT' || o.persona === 'AGENCY',
          )
          .map((o) => ({
            tenantId: o.tenantId,
            displayName: o.name || o.tenantId,
            persona: o.persona as 'CLIENT' | 'AGENCY',
          })),
    },
  );
}
